import Reveal from "@/components/Reveal";
import { waLink } from "@/lib/api";
import { MessageCircle, PenLine, FileCheck2, Users } from "lucide-react";

const STEPS = [
  {
    icon: FileCheck2,
    title: "Compila la dichiarazione dei redditi",
    text: "730, Modello Redditi o Certificazione Unica: vale per tutti, anche per chi ha solo la pensione.",
  },
  {
    icon: PenLine,
    title: "Firma il riquadro dell'8 per mille",
    text: "Basta una firma nell'apposito spazio. Se non firmi, quella quota viene comunque trattenuta: tanto vale scegliere dove va.",
  },
  {
    icon: Users,
    title: "Aiuti chi è in difficoltà",
    text: "I fondi raccolti finanziano lo sportello gratuito, l'assistenza legale di base e il supporto psicologico ai casi più gravi.",
  },
];

export default function OttoPerMille() {
  return (
    <section id="otto-per-mille" data-testid="otto-per-mille-section" className="py-20 lg:py-28 bg-gradient-to-b from-white to-brand-aqua/10">
      <div className="container-site">
        <Reveal className="mb-12">
          <div className="rounded-3xl bg-brand-blue text-white p-8 sm:p-10 shadow-xl shadow-brand-blue/20" data-testid="otto-per-mille-message">
            <p className="text-xl sm:text-2xl font-bold leading-relaxed">
              "Destinando il tuo 8 per mille all'Associazione Vittime del Debito non spendi nulla
              in più, ma ci permetti di aiutare gratuitamente più persone in difficoltà.{" "}
              <span className="text-brand-aqua">Codice fiscale: 14869890963.</span> Basta una firma
              nella tua dichiarazione dei redditi."
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="eyebrow">Sostienici senza spendere</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink">
              L'8 per mille: una firma che{" "}
              <span className="text-brand-aqua">non ti costa nulla</span> e cambia la vita di
              qualcuno.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Ogni anno, con la dichiarazione dei redditi, puoi destinare una piccola quota delle
              tue imposte a finalità sociali. Per te non è una spesa: è semplicemente una scelta su
              chi deve riceverla. Scegliendo l'Associazione Vittime del Debito, ci permetti di
              continuare ad assistere gratuitamente chi non può permettersi un avvocato né uno
              psicologo.
            </p>
            <div className="mt-8 rounded-2xl bg-white border-2 border-brand-aqua/30 px-6 py-5" data-testid="otto-per-mille-cf-box">
              <p className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Per destinare il tuo 8 per mille all'associazione indica
              </p>
              <p className="mt-2 font-extrabold text-brand-ink text-lg">
                Associazione Vittime del Debito
              </p>
              <p className="font-extrabold text-brand-blue text-2xl tracking-wide" data-testid="otto-per-mille-cf">
                C.F. / P.IVA 14869890963
              </p>
            </div>
            <a
              href={waLink("Buongiorno, vorrei informazioni su come destinare l'8 per mille all'Associazione Vittime del Debito (C.F. 14869890963).")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-aqua mt-6"
              data-testid="otto-per-mille-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5" />
              Chiedici come fare su WhatsApp
            </a>
          </Reveal>

          <div className="space-y-5">
            <div className="rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-brand-aqua/10" data-testid="otto-per-mille-image">
              <img
                src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80"
                alt="Bambini sorridenti: il tuo 8 per mille aiuta le famiglie a ripartire"
                loading="lazy"
                className="w-full h-56 object-cover"
              />
            </div>
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <div className="flex gap-5 items-start bg-white rounded-3xl border border-brand-aqua/20 p-6 shadow-sm" data-testid={`otto-per-mille-step-${i + 1}`}>
                  <span className="w-14 h-14 shrink-0 rounded-2xl bg-brand-aqua text-white flex items-center justify-center text-xl font-extrabold">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-bold text-lg text-brand-ink">{s.title}</h3>
                    <p className="mt-1 text-slate-600 leading-relaxed">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
